// Focus-modal preference (PRD 008 Part B). A pure-UI surfacing: an immediate,
// salient modal for a high-salience AI contribution (the screen-viewing case),
// gated by kind + a persisted on/off — the same live-read localStorage pattern
// as the audio-cue mute (audioCues.ts) and auto-speak (speech.ts) prefs. No
// gateway or protocol change: the modal is a second presentation of an existing
// AI-slice turn.

import type { AiTurnKind } from '@/lib/ai/types'

const ENABLED_KEY = 'convariance.focusModal.enabled'
const KINDS_KEY = 'convariance.focusModal.kinds'

/** Kinds that CAN pop the modal. `present` (the AI's direct contribution) and
 *  `caution` (a concrete risk) are the sensible default — the ones a room most
 *  wants unmissable on a shared screen. `note` is excluded (it's status). */
export const FOCUS_KINDS: AiTurnKind[] = ['present', 'caution', 'candidate', 'insight']
const DEFAULT_KINDS: AiTurnKind[] = ['present', 'caution']

/** On by default — the modal is the default surfacing the PRD promotes over the
 *  demoted voice path. */
export function isFocusModalEnabled(): boolean {
  try {
    return localStorage.getItem(ENABLED_KEY) !== '0'
  } catch {
    return true
  }
}

export function setFocusModalEnabled(value: boolean): void {
  try {
    localStorage.setItem(ENABLED_KEY, value ? '1' : '0')
  } catch {
    // private mode — ignore
  }
}

export function getFocusKinds(): AiTurnKind[] {
  try {
    const raw = localStorage.getItem(KINDS_KEY)
    if (!raw) return DEFAULT_KINDS
    const parsed = JSON.parse(raw) as unknown
    if (!Array.isArray(parsed)) return DEFAULT_KINDS
    const kinds = parsed.filter((k): k is AiTurnKind =>
      FOCUS_KINDS.includes(k as AiTurnKind)
    )
    return kinds.length ? kinds : DEFAULT_KINDS
  } catch {
    return DEFAULT_KINDS
  }
}

export function setFocusKinds(kinds: AiTurnKind[]): void {
  try {
    localStorage.setItem(KINDS_KEY, JSON.stringify(kinds))
  } catch {
    // private mode — ignore
  }
}
